import { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/plugins/regions';
import Sidebar from './Sidebar';
import {
  getConversationNext,
  getConversationAudioUrl,
  submitConversationAnnotations,
  skipConversationFile,
} from '../services/api';
import { LABELS, FREE_TEXT_LABEL, toggleLabel, allLabeled, toAnnotations } from '../utils/conversationRegions';

// ── Label styling ──────────────────────────────────────────────────────────
const LABEL_COLOR = {
  off_topic:           '#f59e0b',
  low_engagement:      '#ef4444',
  language_change:     '#3b82f6',
  child_input_ignored: '#a855f7',
  too_slow:            '#eab308',
  high_latency:        '#f97316',
  repetition:          '#14b8a6',
  stops_in_between:    '#ec4899',
  highly_engaged:      '#22c55e',
  others:              '#6b7280',
};
const UNTAGGED_COLOR = 'rgba(248,113,113,0.30)';
const HIDDEN_COLOR   = 'transparent';
const DRAG_COLOR     = 'rgba(255,255,255,0.15)';
const MIN_REGION_LEN = 0.05; // discard accidental click-noise regions below this

function fmtLabel(label) {
  return label.replace(/_/g, ' ');
}

function fmt(sec) {
  if (!isFinite(sec)) return '0:00.0';
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

export default function ConversationAnnotation({ user, onPageChange }) {
  const containerRef = useRef(null);   // WaveSurfer mounts here
  const wsRef         = useRef(null);
  const regionsRef     = useRef(null); // RegionsPlugin instance
  const regionMapRef   = useRef({});   // id -> { id, start, end, labels[], note }, mirrors the plugin's regions
  const durationRef   = useRef(0);
  const regionsShownRef = useRef(true); // mirrors regionsShown, for use inside wavesurfer event handlers

  const [item, setItem]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [done, setDone]               = useState(false);
  const [playing, setPlaying]         = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [zoom, setZoom]               = useState(50);    // px/sec, native wavesurfer zoom
  const [rate, setRate]               = useState(1);
  const [regions, setRegions]         = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  // Hiding regions also makes them click-through, so a new drag can start
  // under/across existing ones (e.g. the exact overlap of two regions).
  const [regionsShown, setRegionsShown] = useState(true);

  const regionColor = useCallback((labels) => {
    if (!regionsShownRef.current) return HIDDEN_COLOR;
    if (!labels.length) return UNTAGGED_COLOR;
    const hexToRgb = (hex) => {
      const n = parseInt(hex.slice(1), 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    };
    const [r, g, b] = labels.reduce(
      (acc, l) => { const [rr, gg, bb] = hexToRgb(LABEL_COLOR[l]); return [acc[0] + rr, acc[1] + gg, acc[2] + bb]; },
      [0, 0, 0]
    ).map(v => Math.round(v / labels.length));
    return `rgba(${r},${g},${b},0.38)`;
  }, []);

  // Must set 'all', not '' — pointer-events is inherited, and wavesurfer's
  // own regionsContainer sets 'none', so clearing the inline override falls
  // back to that inherited 'none' instead of normal clickability.
  function applyInteractivity(region, shown) {
    if (region.element) region.element.style.pointerEvents = shown ? 'all' : 'none';
  }

  function raiseRegion(id) {
    regionsRef.current?.getRegions().forEach(r => {
      if (r.element) r.element.style.zIndex = r.id === id ? '3' : '';
    });
  }

  // wavesurfer's resize handle is a 6px strip fully inside the region's edge.
  // Widening a region drags outward past that boundary, missing the handle
  // and falling through to "create a new region" instead. Widen the handle
  // and center it on the boundary so a click from either side grabs it.
  function enhanceHandles(region) {
    const HANDLE_PX = 12;
    const left = region.element?.querySelector('[part*="region-handle-left"]');
    const right = region.element?.querySelector('[part*="region-handle-right"]');
    if (left) {
      left.style.width = `${HANDLE_PX}px`;
      left.style.marginLeft = `-${HANDLE_PX / 2}px`;
      left.style.background = 'rgba(255,255,255,0.55)';
    }
    if (right) {
      right.style.width = `${HANDLE_PX}px`;
      right.style.marginRight = `-${HANDLE_PX / 2}px`;
      right.style.background = 'rgba(255,255,255,0.55)';
    }
  }

  // ── WaveSurfer + RegionsPlugin init (once) ─────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    let destroyed = false;

    const regionsPlugin = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#5b6675',
      progressColor: '#828d9e',
      cursorColor: '#ffffff',
      cursorWidth: 2,
      height: 130,
      minPxPerSec: 50,
      scrollParent: true,
      normalize: true,
      plugins: [regionsPlugin],
    });
    wsRef.current = ws;
    regionsRef.current = regionsPlugin;
    // Whole-region drag off — its hit zone competed with the resize handle
    // and made grabbing an edge unreliable. Resize still works via enhanceHandles().
    regionsPlugin.enableDragSelection({ color: DRAG_COLOR, drag: false, resize: true });

    ws.on('ready', () => {
      if (destroyed) return;
      const d = ws.getDuration();
      durationRef.current = d;
      setDuration(d);
      setAudioLoading(false);
    });
    ws.on('timeupdate', t => { if (!destroyed) setCurrentTime(t); });
    ws.on('play',   () => { if (!destroyed) setPlaying(true);  });
    ws.on('pause',  () => { if (!destroyed) setPlaying(false); });
    ws.on('finish', () => { if (!destroyed) setPlaying(false); });

    regionsPlugin.on('region-created', r => {
      if (destroyed) return;
      if ((r.end - r.start) < MIN_REGION_LEN) { r.remove(); return; }
      r.setOptions({ color: regionColor([]) });
      applyInteractivity(r, regionsShownRef.current);
      enhanceHandles(r);
      raiseRegion(r.id);
      // Raise on hover too, so adjacent/touching regions don't block each other's handles.
      r.on('over', () => raiseRegion(r.id));
      regionMapRef.current[r.id] = { id: r.id, start: r.start, end: r.end, labels: [], note: '' };
      syncRegions();
    });
    // Fires once when a drag/resize ends. Note: 'region-update-end' doesn't
    // exist as an event name — a listener on it would silently never fire.
    regionsPlugin.on('region-updated', r => {
      if (destroyed) return;
      const existing = regionMapRef.current[r.id];
      regionMapRef.current[r.id] = { id: r.id, start: r.start, end: r.end, labels: existing?.labels || [], note: existing?.note || '' };
      syncRegions();
    });
    regionsPlugin.on('region-clicked', (r, e) => {
      if (destroyed) return;
      e.stopPropagation();
      setSelectedId(r.id);
      highlightRegion(r.id);
      raiseRegion(r.id);
      r.play();
    });

    return () => {
      destroyed = true;
      ws.destroy();
      wsRef.current = null;
      regionsRef.current = null;
      regionMapRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function syncRegions() {
    const ws = regionsRef.current;
    if (ws) {
      ws.getRegions().forEach(r => {
        if ((r.end - r.start) < MIN_REGION_LEN) { r.remove(); delete regionMapRef.current[r.id]; }
      });
    }
    const sorted = Object.values(regionMapRef.current)
      .filter(r => (r.end - r.start) >= MIN_REGION_LEN)
      .sort((a, b) => a.start - b.start);
    setRegions(sorted.map(r => ({ ...r })));
  }

  function highlightRegion(id) {
    regionsRef.current?.getRegions().forEach(r =>
      r.setOptions({ color: r.id === id && regionsShownRef.current ? 'rgba(255,255,255,0.5)' : regionColor(regionMapRef.current[r.id]?.labels || []) })
    );
  }

  function deleteRegion(id) {
    regionsRef.current?.getRegions().find(r => r.id === id)?.remove();
    delete regionMapRef.current[id];
    if (selectedId === id) setSelectedId(null);
    syncRegions();
  }

  function setLabels(id, labels) {
    const existing = regionMapRef.current[id];
    if (!existing) return;
    regionMapRef.current[id] = { ...existing, labels };
    regionsRef.current?.getRegions().find(r => r.id === id)?.setOptions({ color: regionColor(labels) });
    syncRegions();
  }

  function handleToggleLabel(id, label) {
    const current = regionMapRef.current[id]?.labels || [];
    setLabels(id, toggleLabel([{ id, labels: current }], id, label)[0].labels);
  }

  function setNote(id, note) {
    const existing = regionMapRef.current[id];
    if (!existing) return;
    regionMapRef.current[id] = { ...existing, note };
    syncRegions();
  }

  useEffect(() => {
    regionsShownRef.current = regionsShown;
    regionsRef.current?.getRegions().forEach(r => {
      r.setOptions({ color: regionColor(regionMapRef.current[r.id]?.labels || []) });
      applyInteractivity(r, regionsShown);
    });
  }, [regionsShown, regionColor]);

  useEffect(() => { if (duration > 0) wsRef.current?.zoom(zoom); }, [zoom, duration]);
  useEffect(() => { wsRef.current?.setPlaybackRate(rate); }, [rate]);

  // ── Keyboard: play/pause + seek ───────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.code === 'Space')           { e.preventDefault(); wsRef.current?.playPause(); }
      else if (e.code === 'ArrowLeft')  { e.preventDefault(); wsRef.current?.skip(-2); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); wsRef.current?.skip(2); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function seekTo(t) {
    const d = durationRef.current;
    if (d > 0) wsRef.current?.seekTo(Math.min(0.999, t / d));
  }

  // ── Load / submit / skip ──────────────────────────────────────────────────
  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    regionsRef.current?.clearRegions();
    regionMapRef.current = {};
    setRegions([]);
    setSelectedId(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    durationRef.current = 0;
    try {
      const res = await getConversationNext();
      const file = res?.file;
      if (!file) { setDone(true); setLoading(false); return; }
      setItem(file);
      setAudioLoading(true);
      setLoading(false);
      const p = wsRef.current?.load(getConversationAudioUrl(file.id));
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNext(); }, [loadNext]);

  async function handleSubmit() {
    if (!item || !allLabeled(regions)) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitConversationAnnotations(item.id, toAnnotations(regions));
      loadNext();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSkip() {
    if (item) { try { await skipConversationFile(item.id); } catch { /* best effort */ } }
    loadNext();
  }

  const ready = duration > 0;

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="conversation-annotation"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Annotator'}
        subtitle="Conversation Annotation"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <h1 style={{ margin: 0 }}>Conversation Annotation</h1>
        </div>

        {done ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{ fontSize: 48 }}>📭</div>
            <h2>Queue empty</h2>
            <p style={{ color: 'var(--text-muted,#888)' }}>No recordings left to annotate.</p>
            <button className="skip-btn" style={{ width: 'auto', padding: '8px 20px' }}
              onClick={() => { setDone(false); loadNext(); }}>Check again</button>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* File bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>🔊</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{item ? item.filename : 'Loading…'}</span>
              {duration > 0 && (
                <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', marginLeft: 'auto' }}>{fmt(duration)}</span>
              )}
            </div>

            {/* Waveform (regions render natively via RegionsPlugin, overlap included) */}
            <div style={{ position: 'relative', background: '#12161a', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
              {audioLoading && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(10,13,16,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#9ca3af', fontSize: 13 }}>
                  <div style={{ width: 24, height: 24, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'ca-spin 0.7s linear infinite' }} />
                  Loading audio…
                </div>
              )}
              <div ref={containerRef} style={{ padding: '8px 0' }} />
            </div>

            {/* Transport */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TBtn onClick={() => wsRef.current?.seekTo(0)}>⏮</TBtn>
                <TBtn accent onClick={() => wsRef.current?.playPause()}>{playing ? '⏸' : '▶'}</TBtn>
                <TBtn onClick={() => { wsRef.current?.pause(); wsRef.current?.seekTo(0); }}>⏹</TBtn>
                <span style={{ fontSize: 12, color: 'var(--text-muted,#888)', fontVariantNumeric: 'tabular-nums', minWidth: 110 }}>{fmt(currentTime)} / {fmt(duration)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zoom</span>
                <input type="range" min="20" max="500" step="10" value={zoom}
                  onChange={e => setZoom(Number(e.target.value))} style={{ width: 120, accentColor: 'var(--accent)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', minWidth: 48 }}>{zoom}px/s</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Speed</span>
                {[0.5, 0.75, 1, 1.5, 2].map(r => (
                  <button key={r} onClick={() => setRate(r)} style={{
                    background: rate === r ? 'var(--accent)' : 'transparent',
                    border: `1px solid ${rate === r ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit',
                  }}>{r}×</button>
                ))}
              </div>
              <button
                onClick={() => setRegionsShown(v => !v)}
                title={regionsShown ? 'Hide regions on the waveform so you can drag-select a new one underneath/across them (e.g. an overlap slice) — tags are kept, see list below' : 'Show regions on the waveform again'}
                style={{
                  background: regionsShown ? 'var(--accent)' : 'transparent',
                  border: `1px solid ${regionsShown ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 4, color: regionsShown ? '#fff' : 'var(--text)', fontSize: 11, padding: '3px 9px',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>{regionsShown ? '👁 Regions shown' : '🚫 Regions hidden'}</button>
            </div>

            {/* Hint bar */}
            <div style={{ padding: '5px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted,#888)', display: 'flex', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
              {[['Drag waveform', 'New region (regions may overlap)'], ['Drag edges', 'Resize'], ['Click region', 'Select + play'], ['Tag in list', 'below'], ['Regions hidden', 'drag-select a new/overlap region underneath existing ones'], ['Space', 'Play/Pause'], ['←/→', 'Seek ±2s']].map(([k, v]) => (
                <span key={k}><strong style={{ color: 'var(--text)' }}>{k}</strong> {v}</span>
              ))}
            </div>

            {/* Region list — tag / seek / delete each region */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {regions.length === 0 ? (
                <div style={{ padding: '14px 16px', color: 'var(--text-muted,#888)', fontSize: 13 }}>
                  Drag on the waveform to mark a region needing a tag. No regions = no issues found.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg, #0b0e11)' }}>
                      {['#', 'Start', 'End', 'Dur', 'Issue categories', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-muted,#888)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {regions.map((r, i) => (
                      <tr key={r.id}
                        onClick={() => { setSelectedId(r.id); highlightRegion(r.id); raiseRegion(r.id); seekTo(r.start); }}
                        style={{ background: selectedId === r.id ? 'rgba(58,143,104,0.18)' : 'transparent', cursor: 'pointer' }}>
                        <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)' }}>#{i + 1}</td>
                        <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.start)}</td>
                        <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.end)}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)', fontVariantNumeric: 'tabular-nums' }}>{(r.end - r.start).toFixed(2)}s</td>
                        <td style={{ padding: '6px 10px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {LABELS.map(label => {
                              const active = r.labels.includes(label);
                              return (
                                <button key={label}
                                  onClick={() => handleToggleLabel(r.id, label)}
                                  style={{
                                    background: active ? LABEL_COLOR[label] : 'transparent',
                                    border: `1px solid ${LABEL_COLOR[label]}`,
                                    borderRadius: 4, color: active ? '#0b0e11' : LABEL_COLOR[label],
                                    fontSize: 11, fontWeight: active ? 700 : 500, padding: '2px 9px',
                                    cursor: 'pointer', fontFamily: 'inherit',
                                  }}>{fmtLabel(label)}</button>
                              );
                            })}
                          </div>
                          {r.labels.includes(FREE_TEXT_LABEL) && (
                            <input
                              type="text"
                              value={r.note || ''}
                              onChange={e => setNote(r.id, e.target.value)}
                              placeholder="Describe the issue…"
                              style={{
                                marginTop: 5, width: '100%', maxWidth: 320, background: 'var(--bg, #0b0e11)',
                                border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)',
                                fontSize: 11, padding: '4px 7px', fontFamily: 'inherit',
                              }}
                            />
                          )}
                        </td>
                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => deleteRegion(r.id)}
                            title="Delete region"
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted,#888)', fontSize: 11, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}
                          >✕ delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer: status + actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted,#888)' }}>
                {ready ? `${regions.length} region${regions.length !== 1 ? 's' : ''}` : ''}
                {ready && !allLabeled(regions) ? ' · some regions untagged' : ''}
              </span>
              {error && <span style={{ color: '#f87171', fontSize: 12 }}>⚠ {error}</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button className="skip-btn" onClick={handleSkip} disabled={loading || submitting || audioLoading} style={{ width: 'auto', padding: '8px 18px' }}>Skip</button>
                <button className="save-btn" onClick={handleSubmit}
                  disabled={!item || loading || submitting || audioLoading || !ready || !allLabeled(regions)}
                  style={{ width: 'auto', padding: '8px 18px' }}>
                  {submitting ? 'Saving…' : 'Submit →'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <style>{`@keyframes ca-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function TBtn({ children, onClick, accent }) {
  return (
    <button onClick={onClick} style={{
      background: accent ? 'var(--accent)' : 'transparent',
      border: `1px solid ${accent ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 6, color: accent ? '#fff' : 'var(--text)',
      fontSize: accent ? 14 : 16, width: 34, height: 30,
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    }}>{children}</button>
  );
}
